import {
    CUSTOM_ELEMENTS_SCHEMA,
    Component,
    DestroyRef,
    OnInit,
    inject,
    signal,
    ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
    SHUI_MODAL_CONFIG,
    ShButtonComponent,
    ShInputTextComponent,
    ShuiModalConfig,
    ShuiModalRef,
    TextValueAccessor
} from '@gbr-monorepo/gbr-shui';
import { TranslateModule } from '@ngx-translate/core';
import '@shui/core/sh-datepicker';
import { map } from 'rxjs';
import { parseDateValue } from '../../../../shared/utils/parse-date-value';

@Component({
    selector: 'app-release-payout-dialog',
    imports: [ReactiveFormsModule, ShButtonComponent, ShInputTextComponent, TextValueAccessor, TranslateModule],
    templateUrl: './release-payout-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ReleasePayoutDialogComponent implements OnInit {
    protected readonly config = inject<ShuiModalConfig>(SHUI_MODAL_CONFIG);
    private readonly modalRef = inject<ShuiModalRef<{ payoutCutOffDate: Date }>>(ShuiModalRef);
    private readonly destroyRef = inject(DestroyRef);

    protected readonly releasePayoutFormGroup = new FormGroup({
        payoutCutOffDate: new FormControl<string | null>(null, [Validators.required]),
        confirmPayout: new FormControl('', {
            nonNullable: true,
            validators: [Validators.required, Validators.pattern('Payout')]
        })
    });

    protected payoutCutOffDateErrorSignal = signal<string>('');
    protected confirmPayoutErrorSignal = signal<string>('');

    ngOnInit(): void {
        this.initializeErrorMessageListeners();
    }

    protected canConfirm(): boolean {
        const { payoutCutOffDate, confirmPayout } = this.releasePayoutFormGroup.value;
        return parseDateValue(payoutCutOffDate) !== null && confirmPayout === 'Payout';
    }

    public onClose(result?: string): void {
        if (result === 'confirm' && this.canConfirm()) {
            const payoutCutOffDate = this.releasePayoutFormGroup.value.payoutCutOffDate;

            if (payoutCutOffDate) {
                const parsedDate = parseDateValue(payoutCutOffDate);

                if (parsedDate) {
                    this.modalRef.close({ payoutCutOffDate: parsedDate });
                    return;
                }
            }
        }
        this.modalRef.close();
    }

    protected getErrorMessage(control: string): void {
        const errors = Object.keys(this.releasePayoutFormGroup.get(control)?.errors || {});

        if (!errors?.length) {
            this.payoutCutOffDateErrorSignal.set('');
            this.confirmPayoutErrorSignal.set('');
            return;
        }

        const [error] = errors;

        if (error === 'required' && !this.releasePayoutFormGroup.get(control)?.touched) {
            if (control === 'payoutCutOffDate') this.payoutCutOffDateErrorSignal.set('required');
            if (control === 'confirmPayout') this.confirmPayoutErrorSignal.set('required');
        }

        if (error !== 'required' && this.releasePayoutFormGroup.get('confirmPayout').value !== 'Payout') {
            if (control === 'confirmPayout')
                this.confirmPayoutErrorSignal.set(
                    'seasons.season-details.dialogs.release-payout-dialog.errors.confirm-payout'
                );
        }
    }

    private initializeErrorMessageListeners(): void {
        this.releasePayoutFormGroup
            .get('payoutCutOffDate')
            ?.valueChanges.pipe(
                map(() => this.getErrorMessage('payoutCutOffDate')),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        this.releasePayoutFormGroup
            .get('confirmPayout')
            ?.valueChanges.pipe(
                map(() => this.getErrorMessage('confirmPayout')),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    protected onDateChanged(event: Event): void {
        const customEvent = event as CustomEvent<unknown>;
        const target = event.target as (HTMLElement & { standardValue?: unknown[]; value?: string }) | null;
        let value = customEvent.detail as string | string[] | null | undefined;

        if ((value === undefined || value === null) && target) {
            value = target.value ?? target.getAttribute?.('value') ?? undefined;
            if (!value && Array.isArray(target.standardValue) && target.standardValue.length > 0) {
                const first = target.standardValue[0];
                value = first instanceof Date ? first.toISOString().split('T')[0] : (first as string);
            }
        }

        if (Array.isArray(value)) {
            value = value[0] ?? null;
        }

        const normalizedValue = value ?? null;
        const control = this.releasePayoutFormGroup.controls.payoutCutOffDate;

        if (control.value === normalizedValue) {
            return;
        }

        control.setValue(normalizedValue, { emitEvent: false });
        this.getErrorMessage('payoutCutOffDate');
    }
}
